import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";

const ScoringBreakdown = ({ legAId, legBId, visible }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!visible || hasFetched.current || !legAId || !legBId) return;
    hasFetched.current = true;

    const fetchBreakdown = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const res = await ai
          .auth(token)
          .get(`/api/flightLegs/scoringBreakdown/${legAId}/${legBId}`);
        setData(res.data);
      } catch (err) {
        setError("Failed to load scoring breakdown");
      } finally {
        setLoading(false);
      }
    };

    fetchBreakdown();
  }, [visible, legAId, legBId, getAccessTokenSilently]);

  if (!visible) return null;

  if (loading) {
    return (
      <div className="scoring-breakdown__loading">
        <div className="scoring-breakdown__spinner" />
        Computing breakdown...
      </div>
    );
  }

  if (error) {
    return <div className="scoring-breakdown__error">{error}</div>;
  }

  if (!data?.breakdown) {
    return (
      <div className="scoring-breakdown__error">
        {data?.error || "No breakdown available"}
      </div>
    );
  }

  const { breakdown, matchType } = data;

  const getScoreClass = (val) => {
    if (val < 0) return "scoring-breakdown__change--bonus";
    if (val > 0) return "scoring-breakdown__change--penalty";
    return "scoring-breakdown__change--neutral";
  };

  const formatChange = (val) => {
    if (val === 0) return "0";
    return val > 0 ? `+${val}` : `${val}`;
  };

  const coreFactors = breakdown.factors.filter((f) => f.bucket === "core");
  const otherFactors = breakdown.factors.filter((f) => f.bucket !== "core");

  const coreAdjustments = coreFactors.reduce((sum, f) => sum + f.scoreChange, 0);
  const otherAdjustments = otherFactors.reduce((sum, f) => sum + f.scoreChange, 0);
  const totalAdjustments = breakdown.factors.reduce(
    (sum, f) => sum + f.scoreChange,
    0
  );

  const coreBaseScore = breakdown.coreBaseScore ?? 9;
  const corePriority = breakdown.corePriority;
  const rawCoreTotal = breakdown.rawCoreTotal ?? coreBaseScore + coreAdjustments;
  const coreClamped = corePriority != null && corePriority !== rawCoreTotal;

  return (
    <div className="scoring-breakdown">
      <div className="scoring-breakdown__header">
        <span className="scoring-breakdown__title">Score Breakdown</span>
        <span className="scoring-breakdown__match-type">{matchType}</span>
      </div>

      {corePriority != null && (
        <table className="scoring-breakdown__table">
          <thead>
            <tr>
              <th className="scoring-breakdown__th--num">#</th>
              <th>Core Factor</th>
              <th className="scoring-breakdown__th--change">Change</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="scoring-breakdown__row--base">
              <td></td>
              <td>Core Base Score</td>
              <td className="scoring-breakdown__change--neutral">{coreBaseScore}</td>
              <td></td>
            </tr>
            {coreFactors.map((factor, i) => (
              <tr key={`core-${i}`}>
                <td className="scoring-breakdown__num">{i + 1}</td>
                <td>{factor.name}</td>
                <td className={getScoreClass(factor.scoreChange)}>
                  {formatChange(factor.scoreChange)}
                </td>
                <td className="scoring-breakdown__notes">{factor.notes}</td>
              </tr>
            ))}
            <tr className="scoring-breakdown__row--total">
              <td></td>
              <td>
                <strong>Core Score</strong>
              </td>
              <td className="scoring-breakdown__change--neutral">
                <strong>{corePriority} of 9</strong>
              </td>
              <td className="scoring-breakdown__notes">
                Base {coreBaseScore} + adjustments ({formatChange(coreAdjustments)})
                {coreClamped ? ", clamped to min 1" : ""}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <table className="scoring-breakdown__table">
        <thead>
          <tr>
            <th className="scoring-breakdown__th--num">#</th>
            <th>Factor</th>
            <th className="scoring-breakdown__th--change">Change</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr className="scoring-breakdown__row--base">
            <td></td>
            <td>Base Score</td>
            <td className="scoring-breakdown__change--neutral">
              {breakdown.baseScore}
            </td>
            <td></td>
          </tr>
          {otherFactors.map((factor, i) => (
            <tr key={`other-${i}`}>
              <td className="scoring-breakdown__num">{i + 1}</td>
              <td>{factor.name}</td>
              <td className={getScoreClass(factor.scoreChange)}>
                {formatChange(factor.scoreChange)}
              </td>
              <td className="scoring-breakdown__notes">{factor.notes}</td>
            </tr>
          ))}
          <tr className="scoring-breakdown__row--total">
            <td></td>
            <td>
              <strong>Final Score</strong>
            </td>
            <td className="scoring-breakdown__change--neutral">
              <strong>{breakdown.priority}</strong>
            </td>
            <td className="scoring-breakdown__notes">
              Base {breakdown.baseScore} + core ({formatChange(coreAdjustments)}) + other ({formatChange(otherAdjustments)}) = {formatChange(totalAdjustments)}
              {breakdown.priority !== breakdown.rawTotal
                ? ", clamped to min 1"
                : ""}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ScoringBreakdown;
